import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { BasketballLeague, CustomTableColumn } from '@hefe/models';
import { BasketballLeaguesSlice, ModalsSlice } from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import { Button, message, Popconfirm } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import CustomTable from '../../core/components/table/custom-table.component';
import AddLeagueAssistent from '../components/add-league-assistent/add-league-assistent.component';
import EditLeagueForm from '../forms/edit-league/edit-league.form';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface LeaguesPageProps {}

export default function LeaguesPage(props: LeaguesPageProps) {
  const dispatch = useAppDispatch();
  const leagues = useSelector(BasketballLeaguesSlice.basketballLeaguesSelector);

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncingLeague, setSyncingLeague] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    fetchBasketballLeagues();
  }, []);

  const fetchBasketballLeagues = () => {
    setLoading(true);
    dispatch(BasketballLeaguesSlice.fetchBasketballLeagues())
      .unwrap()
      .finally(() => setLoading(false));
  };

  const syncLeague = () => {
    if (selectedRowKeys.length !== 1) {
      return;
    }

    const leagueId = parseInt(selectedRowKeys[0]);

    setSyncingLeague(true);
    dispatch(BasketballLeaguesSlice.syncBasketballLeague(leagueId))
      .unwrap()
      .then(() => {
        message.success(t('leagues.sync.success'));
        setSelectedRowKeys([]);
      })
      .catch(() => {})
      .finally(() => setSyncingLeague(false));
  };

  const deleteLeague = (id: number) => {
    const league = leagues.find((league) => league.id === id);
    if (league) {
      setDeleting(true);
      dispatch(BasketballLeaguesSlice.deleteBasketballLeague(league))
        .unwrap()
        .finally(() => setDeleting(false));
    }
  };

  const openAddLeagueAssistent = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: t('leagues.connect'),
        component: <AddLeagueAssistent />,
        width: 1000,
        hideOkButton: true,
        hideCancelButton: true,
      })
    );
  };

  const openLeague = (league: BasketballLeague) => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <EditLeagueForm league={league} />,
        formId: `edit-league-form-${league.id}`,
        title: t('leagues.edit'),
      })
    );
  };

  const columns: CustomTableColumn[] = [
    {
      key: 'name',
      title: t('name'),
      dataIndex: 'name',
      sortType: 'string',
      default: true,
      showFilter: true,
    },
    {
      key: 'teams',
      title: t('teams'),
      default: true,
      render: (league: BasketballLeague) => {
        return league.teams?.map((team) => team.name).join(', ');
      },
    },
    {
      key: 'last_synced_at',
      title: t('last_synced_at'),
      default: true,
      render: (league: BasketballLeague) => {
        return league.last_synced_at
          ? formatDateTime(league.last_synced_at)
          : t('never');
      },
    },
  ];

  return (
    <CustomTable
      rowKey={'id'}
      columns={columns}
      dataSource={leagues}
      name="leagues"
      loading={loading}
      displayToolbarItemsFirst
      onRowClick={(item: BasketballLeague) => openLeague(item)}
      selectedRowKeys={selectedRowKeys}
      onRowSelect={(keys) => setSelectedRowKeys(keys)}
      toolbarItems={[
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openAddLeagueAssistent()}
        />,
        <Popconfirm
          title={t('leagues.sync.title')}
          description={t('leagues.sync.question')}
          disabled={selectedRowKeys.length !== 1}
          onConfirm={() => syncLeague()}
          overlayStyle={{ maxWidth: 400 }}
        >
          <Button
            icon={<SyncOutlined />}
            disabled={selectedRowKeys.length !== 1}
            loading={syncingLeague}
          />
        </Popconfirm>,
      ]}
      deleteAction={{
        action: (keys: any[]) => (keys[0] ? deleteLeague(keys[0]) : null),
        loading: deleting,
      }}
    />
  );
}
